import { useCallback, useEffect, useState } from "react";
import { listInvitations } from "../api/invitations";
import type { Invitation } from "../types";

interface UseInvitations {
  invitations: Invitation[];
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

// Mirrors Team.vue's `_createInvitationLinks()` — general-api doesn't
// return an invitation link, the client constructs it from
// `${presenterDomain}/pages/register?invite-code=${id}`.
const PRESENTER_DOMAIN: string | undefined = import.meta.env.VITE_PRESENTER_DOMAIN;

function buildInvitationLink(id: Invitation["id"]): string | undefined {
  if (!PRESENTER_DOMAIN) return undefined;
  const base = PRESENTER_DOMAIN.replace(/\/$/, "");
  return `${base}/pages/register?invite-code=${encodeURIComponent(String(id))}`;
}

function withLink(invitation: Invitation): Invitation {
  return invitation.link
    ? invitation
    : { ...invitation, link: buildInvitationLink(invitation.id) };
}

export function useInvitations(): UseInvitations {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await listInvitations({ signal: controller.signal });
        setInvitations(res.map(withLink));
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [tick]);

  return { invitations, loading, error, refresh };
}
